/**
 * PATH: packages/kanban-host/src/allocation/boardPrefs.ts
 * FILE_ID: AIHUB-KANBAN-ALLOC-PREFS-001
 * PURPOSE: Per-user pinned + recently-used Kanban board ids, persisted
 *          to localStorage. Drives the "Allocate to Kanban" submenu's
 *          ordering so the operator's top-of-mind boards bubble to the
 *          top of the list.
 * WHY: Module B of EMAIL_KANBAN_ALLOCATION_PLAN.md. Server-side
 *      "favourites" don't exist yet on `Board`; localStorage gets us
 *      the immediate UX win and lets us migrate to a BE column later
 *      without changing the public API surface here.
 * FLOW:
 *   readPinned/Recent  →  ordered string[] of board ids (newest first)
 *   togglePinned/touch →  void  (writes localStorage + emits event)
 *   subscribePrefs     →  unsubscribe fn  (drives in-page reactivity)
 *
 * DEPENDENCIES: window.localStorage, window CustomEvent
 *
 * Hard limits enforced here (so future authors don't drift):
 *   - max 6 pinned boards per org (matches the visible-slots in the menu)
 *   - max 8 recents per org   (anything older falls off LRU-style)
 *   - corrupt JSON falls back to []; never throws to the caller
 *
 * Key shape on disk:
 *
 *   v1 (legacy, per-(org)):
 *     kanban:pinned-boards:<orgId>          →  '["<board_id>", …]'
 *     kanban:recent-boards:<orgId>          →  '["<board_id>", …]'
 *
 *   v2 (postmortem 2026-05-19 follow-up #6 / qf!3196):
 *     kanban:pinned-boards-v2:<userId>:<orgId>  →  '["<board_id>", …]'
 *     kanban:recent-boards-v2:<userId>:<orgId>  →  '["<board_id>", …]'
 *
 * INCIDENT 2026-05-19 (postmortem follow-up #6):
 *   The v1 keys were org-scoped only. Two operators sharing a
 *   browser (rare in private operator life, common in admin/IT
 *   support flows) saw each other's pins. Worse, an org-id
 *   renumber on the BE (planned for the multi-tenant rollout)
 *   would invalidate every pin without warning. The v2 keys add
 *   the master-Quotech user id as a leading scope, so:
 *     - operator A and B sharing a browser keep separate pins
 *     - an org-id renumber forces a fresh pin set (acceptable —
 *       we don't promise pin migration on org id changes)
 *     - cross-tenant access (same operator, different tenant)
 *       still gets isolated pins because the org segment changes
 *
 * Migration semantics:
 *   When a v2 read returns an empty list AND a v1 read for the
 *   same orgId returns ids, the helpers DO NOT auto-migrate or
 *   write anything to v2. The legacy ids are returned READ-ONLY.
 *   The next mutation (toggle / touch) writes to v2; v1 keys are
 *   left in place indefinitely so a downgrade or a stale-bundle
 *   tab still sees the operator's existing pins.
 *
 *   This is the expand → migrate → contract pattern from
 *   `04_multi_agent.mdc`: phase 1 ships dual-read, phase 2 (later
 *   MR) migrates writers, phase 3 (much later) deletes v1 keys
 *   in `wipeTenantState`. We are at phase 1 here.
 *
 *   When `userId` is null/undefined the helpers FALL BACK to v1
 *   behaviour entirely (no v2 read, no v2 write). This keeps the
 *   API backwards-compatible for shells that haven't been wired
 *   to pass the master user id yet.
 *
 * Which `orgId` to pass (BUG-2026-05-13-ALLOCATE-MENU-PIN-SPLIT-BRAIN):
 *
 *   Use the SAME source the Kanban LEFT RAIL uses for its pinned
 *   section — `session?.organisation?.id` (the LOCAL per-tenant org
 *   id from the session payload). The rail's pinned-board reader in
 *   `packages/ui/src/kanban/components/KanbanBoardList.tsx` keys on
 *   the same prefix with this id; passing a DIFFERENT id (e.g. the
 *   master Quotech org id from `resolveQuotechTenantId()`) creates a
 *   split-brain store where pins added in one surface are invisible
 *   in the other.
 *
 *   Why not the master org id (the original intent): the master id
 *   would be correct for cross-tenant isolation, but the rail
 *   shipped first using the local id, and aligning all surfaces on
 *   the local id is the lowest-risk migration. For SINGLE-TENANT
 *   bundles (Newmark) the two ids are stable constants — either
 *   choice works in isolation, but they MUST agree across surfaces.
 *
 *   Future cross-tenant unification (when a multi-tenant bundle
 *   reuses the SAM kanban package) should standardise to the master
 *   id everywhere, with a one-shot localStorage migration that
 *   reads from the local-id keys and merges into the master-id
 *   keys. Not done here because Newmark is single-tenant.
 */

const MAX_PINNED = 6
const MAX_RECENT = 8

const PINNED_PREFIX = 'kanban:pinned-boards:'
const RECENT_PREFIX = 'kanban:recent-boards:'

/** v2 prefixes (per-(user, org)). See file header for migration
 *  semantics. */
const PINNED_PREFIX_V2 = 'kanban:pinned-boards-v2:'
const RECENT_PREFIX_V2 = 'kanban:recent-boards-v2:'

const EVENT_NAME = 'quotech:kanban-board-prefs-changed'

/** Type of `userId` accepted by the public API. We allow string |
 * number because:
 *  - master JWT `sub` is a string
 *  - master JWT `user_id` is a numeric Django PK
 *  - tenant session `user.id` is a numeric local PK
 * Coercion to the on-disk segment happens in `userSegment()`. */
export type UserScope = string | number | null | undefined

function userSegment(userId: UserScope): string | null {
  if (userId === null || userId === undefined) return null
  const trimmed = String(userId).trim()
  if (!trimmed) return null
  // Disallow ":" in the segment so future authors can't accidentally
  // collide with the prefix delimiter. We swap to "_" silently —
  // user ids in our system never contain colons, but a defensive
  // sanitiser is cheap.
  return trimmed.replace(/:/g, '_')
}

// ──────────────────────────────────────────────────────────
// Internal: safe read / write helpers
// ──────────────────────────────────────────────────────────

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    const probe = '__quotech_kanban_prefs_probe__'
    window.localStorage.setItem(probe, '1')
    window.localStorage.removeItem(probe)
    return window.localStorage
  } catch {
    return null
  }
}

function readList(key: string): string[] {
  const storage = safeStorage()
  if (!storage) return []
  try {
    const raw = storage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((x): x is string => typeof x === 'string' && x.length > 0)
  } catch {
    return []
  }
}

function writeList(key: string, list: string[]): void {
  const storage = safeStorage()
  if (!storage) return
  try {
    storage.setItem(key, JSON.stringify(list))
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { key } }))
    }
  } catch {
    // Quota / privacy mode — fall through silently. The in-memory
    // UI state still updates because the hook reads writeList's
    // *next* call's returned list, not localStorage directly.
  }
}

function pinnedKey(orgId: number | null): string {
  return `${PINNED_PREFIX}${orgId ?? 'anon'}`
}

function recentKey(orgId: number | null): string {
  return `${RECENT_PREFIX}${orgId ?? 'anon'}`
}

function pinnedKeyV2(userId: UserScope, orgId: number | null): string | null {
  const u = userSegment(userId)
  if (!u) return null
  return `${PINNED_PREFIX_V2}${u}:${orgId ?? 'anon'}`
}

function recentKeyV2(userId: UserScope, orgId: number | null): string | null {
  const u = userSegment(userId)
  if (!u) return null
  return `${RECENT_PREFIX_V2}${u}:${orgId ?? 'anon'}`
}

// ──────────────────────────────────────────────────────────
// Pinned boards (operator-controlled favourites)
// ──────────────────────────────────────────────────────────

/**
 * Scan ALL `kanban:pinned-boards:*` keys in localStorage and return
 * the union of their ids. Belt-and-braces fallback for the case
 * where the user's pin lives under a DIFFERENT orgId variant than
 * the one the caller passed (see BUG-2026-05-13-ALLOCATE-MENU-PIN-
 * SPLIT-BRAIN). Two ways a pin can land under a "different" key:
 *
 *   1. Two surfaces (the Kanban rail in `packages/ui` and this
 *      submenu) historically used different orgId sources
 *      (`session.organisation.id` vs `resolveQuotechTenantId()`),
 *      which evaluate to different integers for the same user.
 *      Older sessions therefore wrote to one key, newer sessions
 *      read from the other.
 *
 *   2. The user pinned in one tab while session-loading was
 *      mid-flight (`organisationId === undefined`), writing
 *      nothing — or wrote to an "anon" / stale-token key.
 *
 * For a SINGLE-TENANT bundle (Newmark) the union is safe: every
 * pinned id belongs to the same operator/tenant regardless of
 * which orgId variant scoped it. For a multi-tenant bundle this
 * helper is a no-op fallback (only used when the primary key is
 * empty) so we never bleed pins across tenants when the primary
 * read succeeded.
 */
function scanAllPinnedKeys(): string[] {
  const storage = safeStorage()
  if (!storage) return []
  const ids = new Set<string>()
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (!key || !key.startsWith(PINNED_PREFIX)) continue
      for (const id of readList(key)) ids.add(id)
    }
  } catch {
    return []
  }
  return Array.from(ids)
}

/**
 * Read pinned board ids for the given (user, org) scope.
 *
 * Resolution order:
 *   1. v2 key (per-(user, org)) — if a userId was provided AND the
 *      key has data, return it. This is the post-migration path.
 *   2. v1 key (per-org) — legacy fallback for operators who pinned
 *      before the v2 keys existed. Returned READ-ONLY (no
 *      auto-migration to v2 — that happens on next mutation).
 *   3. Cross-key scan — last-resort fallback for the
 *      BUG-2026-05-13-ALLOCATE-MENU-PIN-SPLIT-BRAIN case where
 *      pins landed under a different org-id variant.
 *
 * Tolerating `userId === null/undefined` keeps the public API
 * backwards-compatible for callers that haven't been wired to pass
 * the master user id yet — they get exactly v1 behaviour. */
export function readPinnedBoardIds(
  orgId: number | null,
  userId: UserScope = null,
): string[] {
  const v2Key = pinnedKeyV2(userId, orgId)
  if (v2Key) {
    const v2 = readList(v2Key).slice(0, MAX_PINNED)
    if (v2.length > 0) return v2
  }
  const primary = readList(pinnedKey(orgId)).slice(0, MAX_PINNED)
  if (primary.length > 0) return primary
  // Fallback: scan all variants. Capped to MAX_PINNED for sanity.
  return scanAllPinnedKeys().slice(0, MAX_PINNED)
}

/**
 * Add / remove a board from pinned. Returns the resulting list so the
 * caller can update local state without a second read.
 *
 * When `userId` is provided we write to the v2 key only. The v1 key
 * is left UNTOUCHED so a downgraded bundle in another tab still
 * sees the same toggled state from before the upgrade. (We do NOT
 * delete the v1 entry — the migration table in
 * `wipeTenantState`'s WIPE_PATTERNS will sweep it up at sign-out
 * time, which is the only safe moment to drop legacy keys.)
 *
 * When `userId` is null/undefined we fall back to v1 behaviour:
 * write to the v1 key only (legacy contract preserved).
 */
export function togglePinnedBoard(
  orgId: number | null,
  boardId: string,
  userId: UserScope = null,
): string[] {
  const v2Key = pinnedKeyV2(userId, orgId)
  if (v2Key) {
    // First mutation under v2 — seed from whatever the legacy
    // key contained so the operator's pre-upgrade pins survive
    // the toggle. Read order matches readPinnedBoardIds: prefer
    // v2 itself, fall back to v1, ignore the cross-scan
    // here (we don't want to leak split-brain v1 ids into the
    // user-scoped key).
    const seed = readList(v2Key)
    const current =
      seed.length > 0 ? seed : readList(pinnedKey(orgId))
    const exists = current.includes(boardId)
    const next = exists
      ? current.filter((id) => id !== boardId)
      : [boardId, ...current].slice(0, MAX_PINNED)
    writeList(v2Key, next)
    return next
  }
  // v1-only path (no userId provided)
  const key = pinnedKey(orgId)
  const current = readList(key)
  const exists = current.includes(boardId)
  let next: string[]
  if (exists) {
    next = current.filter((id) => id !== boardId)
  } else {
    next = [boardId, ...current].slice(0, MAX_PINNED)
  }
  writeList(key, next)
  return next
}

export function isPinnedBoard(
  orgId: number | null,
  boardId: string,
  userId: UserScope = null,
): boolean {
  return readPinnedBoardIds(orgId, userId).includes(boardId)
}

// ──────────────────────────────────────────────────────────
// Recent boards (LRU touched on every allocate/open)
// ──────────────────────────────────────────────────────────

/** Same fallback shape as {@link scanAllPinnedKeys} — see its docs
 *  for the rationale. Recents are user-LRU so unioning across
 *  orgId variants is safe for single-tenant bundles. */
function scanAllRecentKeys(): string[] {
  const storage = safeStorage()
  if (!storage) return []
  const ids = new Set<string>()
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (!key || !key.startsWith(RECENT_PREFIX)) continue
      for (const id of readList(key)) ids.add(id)
    }
  } catch {
    return []
  }
  return Array.from(ids)
}

export function readRecentBoardIds(
  orgId: number | null,
  userId: UserScope = null,
): string[] {
  const v2Key = recentKeyV2(userId, orgId)
  if (v2Key) {
    const v2 = readList(v2Key).slice(0, MAX_RECENT)
    if (v2.length > 0) return v2
  }
  const primary = readList(recentKey(orgId)).slice(0, MAX_RECENT)
  if (primary.length > 0) return primary
  return scanAllRecentKeys().slice(0, MAX_RECENT)
}

/**
 * Bump a board to the head of the recents list. Idempotent — a board
 * that's already #1 stays at #1 (no churn / no event spam).
 */
export function touchRecentBoard(
  orgId: number | null,
  boardId: string,
  userId: UserScope = null,
): string[] {
  const v2Key = recentKeyV2(userId, orgId)
  if (v2Key) {
    const seed = readList(v2Key)
    const current = seed.length > 0 ? seed : readList(recentKey(orgId))
    if (current[0] === boardId) return current.slice(0, MAX_RECENT)
    const next = [boardId, ...current.filter((id) => id !== boardId)].slice(
      0,
      MAX_RECENT,
    )
    writeList(v2Key, next)
    return next
  }
  // v1-only path
  const key = recentKey(orgId)
  const current = readList(key)
  if (current[0] === boardId) return current.slice(0, MAX_RECENT)
  const next = [boardId, ...current.filter((id) => id !== boardId)].slice(0, MAX_RECENT)
  writeList(key, next)
  return next
}

/**
 * Clear an explicit board from recents (used when a board is archived
 * or the operator hits "Forget this board" in a future settings UI).
 */
export function forgetRecentBoard(
  orgId: number | null,
  boardId: string,
  userId: UserScope = null,
): string[] {
  const v2Key = recentKeyV2(userId, orgId)
  if (v2Key) {
    const seed = readList(v2Key)
    const current = seed.length > 0 ? seed : readList(recentKey(orgId))
    if (!current.includes(boardId)) return current
    const next = current.filter((id) => id !== boardId)
    writeList(v2Key, next)
    return next
  }
  // v1-only path
  const key = recentKey(orgId)
  const current = readList(key)
  if (!current.includes(boardId)) return current
  const next = current.filter((id) => id !== boardId)
  writeList(key, next)
  return next
}

// ──────────────────────────────────────────────────────────
// Cross-tab + in-tab reactivity
// ──────────────────────────────────────────────────────────

/**
 * Subscribe to any in-tab or cross-tab changes to pinned / recent
 * board prefs. Returns an unsubscribe fn. Hooks call this so the
 * menu re-orders the moment a board is pinned from elsewhere.
 */
export function subscribePrefs(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}

  const onCustom = () => listener()
  const onStorage = (event: StorageEvent) => {
    if (!event.key) return
    if (
      event.key.startsWith(PINNED_PREFIX) ||
      event.key.startsWith(RECENT_PREFIX) ||
      event.key.startsWith(PINNED_PREFIX_V2) ||
      event.key.startsWith(RECENT_PREFIX_V2)
    ) {
      listener()
    }
  }

  window.addEventListener(EVENT_NAME, onCustom as EventListener)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(EVENT_NAME, onCustom as EventListener)
    window.removeEventListener('storage', onStorage)
  }
}

export const __kanbanPrefsInternals = {
  PINNED_PREFIX,
  RECENT_PREFIX,
  PINNED_PREFIX_V2,
  RECENT_PREFIX_V2,
  EVENT_NAME,
  MAX_PINNED,
  MAX_RECENT,
}
